import { useState, useEffect } from 'react';
import { getAuthStatus } from '@/shared/api/authentication-api';
import { UserType } from '@/types/authentication';
import { getErrorMessage } from '@/shared/errors/app-error';
import { setErrorReportingUser } from '@/shared/errors/error-reporter';

/*
TODO: We should utilize Tanstack Query here instead and use this function as the queryFn
TODO: This way we can re-use this hook anywhere else in the app with better performance
*/

interface Client {
  id: string;
  name: string;
  region?: string;
  beta_features_enabled?: boolean;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  job_title: string;
  client?: number;
  client_info?: Client;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  is_client_admin: boolean;
  user_type: UserType;
  last_login?: string | null;
  date_joined: string;
  prefers_dark: boolean;
}

function applyTheme(dark: boolean) {
  if (dark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export const useUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await getAuthStatus();

        if (data.isAuthenticated && data.user) {
          const userData: User = {
            id: String(data.user.id),
            email: data.user.email,
            first_name: data.user.first_name,
            last_name: data.user.last_name,
            phone_number: data.user.phone_number,
            job_title: data.user.job_title,
            client: data.user.client,
            client_info: data.user.client_info,
            is_active: data.user.is_active,
            is_staff: data.user.is_staff,
            is_superuser: data.user.is_superuser,
            is_client_admin: data.user.is_client_admin ?? false,
            user_type: data.user.user_type,
            last_login: data.user.last_login,
            date_joined: data.user.date_joined,
            prefers_dark: data.user.prefers_dark ?? false,
          };
          setUser(userData);
          setErrorReportingUser(userData.id);
          applyTheme(userData.prefers_dark);
        } else {
          setUser(null);
          setErrorReportingUser(null);
        }
      } catch (err) {
        console.error('User fetch error:', err);
        setError(getErrorMessage(err));
        setUser(null);
        setErrorReportingUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  return { user, loading, error };
};
